import * as paymentRequestHooks from "@rentiohq/shared-frontend/dist/reduxV2/paymentRequest/paymentRequest.hooks";
import { EPaymentRequestStatus } from "@rentiohq/shared-frontend/dist/types/paymentRequest.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import * as paymentRequestUtils from "@rentiohq/shared-frontend/dist/utils/paymentRequest.utils";
import { EAssets } from "@rentiohq/web-shared-next/dist/assets";
import {
  Button,
  Icon,
  MainCard,
  SpinningLoader,
  Typography,
} from "@rentiohq/web-shared-next/dist/ui-components";
import { EButtonVariants } from "@rentiohq/web-shared-next/dist/utils/types/button";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PaymentStatusStyles } from "./PaymentStatus.styles";

let fetchDataInterval: NodeJS.Timeout | undefined;

export const PaymentStatus = () => {
  const navigate = useNavigate();
  const params = useParams();

  const sx = PaymentStatusStyles();

  const paymentRequestId = params.paymentRequestId
    ? parseInt(params.paymentRequestId, 0)
    : undefined;

  const {
    detail: paymentRequest,
    refetch,
    isFetching,
  } = paymentRequestHooks.useDetail({
    shouldRefetch: false,
    id: paymentRequestId,
  });

  const fetchData = React.useCallback(() => {
    if (!paymentRequestId) {
      return;
    }

    if (isFetching) {
      return;
    }

    // Only fetch new data for new or approved payment request
    if (
      paymentRequest &&
      paymentRequest.status !== EPaymentRequestStatus.New &&
      paymentRequest.status !== EPaymentRequestStatus.Approved
    ) {
      return;
    }

    refetch();
  }, [paymentRequest]);

  // Lifecycle
  React.useEffect(() => {
    fetchDataInterval = setInterval(fetchData, 2500);

    return () => {
      if (fetchDataInterval) {
        clearInterval(fetchDataInterval);
      }
    };
  }, [paymentRequest]);

  const getColor = (status: EPaymentRequestStatus) => {
    switch (status) {
      case EPaymentRequestStatus.Paid:
        return "#1ab394";
      case EPaymentRequestStatus.Failed:
        return "#d54c48";
      default:
        return "#ec9c3d";
    }
  };

  const renderStatus = (
    status: EPaymentRequestStatus,
    title: string,
    info?: string,
  ) => {
    return (
      <>
        <Icon
          path={EAssets.PaymentSuccess}
          realColor={getColor(status)}
          width="188px"
          height="188px"
        />
        <Typography variant="h2" mt={12.5}>
          {title}
        </Typography>
        {info && (
          <Typography variant="body1" mt={5} sx={sx.content.info}>
            {info}
          </Typography>
        )}
      </>
    );
  };

  const renderPaymentStatus = () => {
    switch (paymentRequest?.status) {
      case EPaymentRequestStatus.New:
      case EPaymentRequestStatus.Approved:
        return renderStatus(EPaymentRequestStatus.New, "Payment is pending");

      // All 'success' states
      case EPaymentRequestStatus.Pending:
      case EPaymentRequestStatus.PaidIn:
      case EPaymentRequestStatus.Partial:
      case EPaymentRequestStatus.Transferring:
      case EPaymentRequestStatus.Transferred:
      case EPaymentRequestStatus.PayingOut:
      case EPaymentRequestStatus.Paid: {
        const postfix = paymentRequest.paymentMethod;

        const sepaBankDaysOffset =
          paymentRequestUtils.sepaDeductionDaysFromNow(paymentRequest);

        const titleKeyBase = "payment.feedback.success.title";
        let titleKey = postfix ? `${titleKeyBase}.${postfix}` : titleKeyBase;
        // Untranslated for payment method => use base key
        if (titleKey === getLocalizedText(titleKey)) {
          titleKey = titleKeyBase;
        }
        const title = getLocalizedText(titleKey, {
          sepaBankDaysOffset: `${sepaBankDaysOffset}`,
        });

        const infoKeyBase = "payment.feedback.success.info";
        let infoKey = postfix ? `${infoKeyBase}.${postfix}` : infoKeyBase;
        // Untranslated for payment method => use base key
        if (infoKey === getLocalizedText(infoKey)) {
          infoKey = infoKeyBase;
        }
        const info = getLocalizedText(infoKey, {
          sepaBankDaysOffset: `${sepaBankDaysOffset}`,
        });

        return renderStatus(EPaymentRequestStatus.Paid, title, info);
      }

      case EPaymentRequestStatus.FailedTransfer:
      case EPaymentRequestStatus.FailedPaidIn:
      case EPaymentRequestStatus.FailedPayout:
      case EPaymentRequestStatus.FailedKyc:
      case EPaymentRequestStatus.Failed:
        return renderStatus(
          EPaymentRequestStatus.Failed,
          getLocalizedText("payment.feedback.error.title"),
          getLocalizedText("payment.feedback.error.info"),
        );

      default:
        return <></>;
    }
  };

  return (
    <MainCard contentSX={sx.wrapper}>
      {isFetching ? (
        <SpinningLoader />
      ) : (
        <>
          {renderPaymentStatus()}
          <Button
            variant={EButtonVariants.Contained}
            onClick={() => navigate("/payments")}
            sx={sx.content.close}
          >
            {getLocalizedText("system.close")}
          </Button>
        </>
      )}
    </MainCard>
  );
};
